import React, { useEffect, useState } from 'react';
import { Button, Divider, IconButton, styled, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import phone from '../../../src/assets/Schedule/phone1.svg';
import calender from '../../../src/assets/Schedule/SVG (1).svg'
import reschedule from '../../../src/assets/Schedule/reschedule.svg';
import checkout from '../../../src/assets/Schedule/checkout.svg'
import payment from '../../../src/assets/Schedule/taskbutton.svg';
import dollar from '../../../src/assets/Schedule/dollar.svg';
import MicIcon from '@mui/icons-material/Mic';
import { useNavigate } from "react-router-dom";
// dollar
import timer from '../../../src/assets/Schedule/time.svg'
import dayjs from 'dayjs';
import './box.css';

import api from '../../api/api';
import AppointmentCard from './AppointmentCard';
import ToastMessage from '../../utils/mui/righttoptoast';
import ConfirmCheckoutModal from './chcekoutconformmodal';
import { calendericonblue } from '../../utils/svgs/svgcode';
import { outlinedbluecalender,outlinedblueclock } from '../../utils/svgs/svgcode';
import ConfirmCancelModal from './cancelConformationModal';
import { getPracticeId, patientPhotoUrl, practiceShortName } from '../../config/practice';
import { categoryOf, initialsOf, prettyType } from '../apptTypes';

/* ---------------------------------------------------------------------------
   Presentation helpers for the patient/appointment panel. These only draw
   chips, tiles and glyphs - every handler, request and piece of state stays
   in CreateAppointment below, unchanged.

   The imported Schedule svgs (calender / phone / dollar) are stroked white for
   the old teal header, so they are invisible on the new white one; the header
   glyphs below are inline and inherit currentColor instead.
--------------------------------------------------------------------------- */

const Glyph = ({ size = 14, children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flex: 'none' }}
  >
    {children}
  </svg>
);

const CalendarGlyph = (props) => (
  <Glyph {...props}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
  </Glyph>
);

const PhoneGlyph = (props) => (
  <Glyph {...props}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  </Glyph>
);

const ShieldGlyph = (props) => (
  <Glyph {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Glyph>
);

const ClockGlyph = (props) => (
  <Glyph {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Glyph>
);

const PlusGlyph = (props) => (
  <Glyph {...props}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
);

const ClipboardGlyph = (props) => (
  <Glyph {...props}>
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M9 12h6M9 16h4" />
  </Glyph>
);

const CalendarClockGlyph = (props) => (
  <Glyph {...props}>
    <path d="M8 2v4M16 2v4M3 10h11" />
    <path d="M20 12.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h6.5" />
    <circle cx="18" cy="18" r="4" />
    <path d="M18 16.6V18l1 .9" />
  </Glyph>
);

// "In 6 weeks" / "9 days ago". dayjs' relativeTime plugin is not extended
// app-wide and extending it here would reach every other screen, so this is
// worked out by hand.
const relativeWhen = (value) => {
  const d = dayjs(value);
  if (!value || !d.isValid()) return '';
  const days = d.startOf('day').diff(dayjs().startOf('day'), 'day');
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  const n = Math.abs(days);
  const [count, unit] =
    n < 14 ? [n, 'day'] : n < 60 ? [Math.round(n / 7), 'week'] : [Math.round(n / 30), 'month'];
  const label = `${count} ${unit}${count === 1 ? '' : 's'}`;
  return days > 0 ? `In ${label}` : `${label} ago`;
};

// Same expression the old markup used, kept as-is so odd times still print
// exactly what they printed before.
const timeLabel = (starttime) => dayjs(`1970-01-01T${starttime}`).format('h:mm A');

// A row the practice has already been through, so it belongs under Past visits
// rather than Upcoming. Today counts as upcoming.
const isPastRow = (row) => {
  const d = dayjs(row?.startdate);
  return d.isValid() && d.startOf('day').isBefore(dayjs().startOf('day'));
};

const statusTone = (status) => {
  const v = String(status || '').toLowerCase();
  if (/cancel|no[\s-]?show/.test(v)) return 'bg-[#FEF2F2] text-[#C10007] border-[#FFC9C9]';
  if (/checked out|completed/.test(v)) return 'bg-[#E8F6EE] text-[#1F7A42] border-[#CBE9D7]';
  return 'bg-[#F1F3F7] text-[#48526A] border-[#E3E8EF]';
};

const StatusChip = ({ status }) =>
  status ? (
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-2.5 py-[3px] font-inter text-[11.5px] font-semibold ${statusTone(
        status
      )}`}
    >
      <span className="h-[6px] w-[6px] flex-none rounded-full bg-current" />
      <span className="truncate">{status}</span>
    </span>
  ) : null;

const TypeChip = ({ type }) => {
  const label = prettyType(type);
  if (!label) return null;
  const cat = categoryOf(type);
  return (
    <span
      className="inline-flex min-w-0 max-w-full items-center rounded-full border px-2.5 py-[3px] font-inter text-[11.5px] font-semibold"
      style={{ background: cat.tint, color: cat.color, borderColor: `${cat.color}33` }}
    >
      <span className="truncate">{label}</span>
    </span>
  );
};

const ProviderChip = ({ name }) =>
  name ? (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-[#E3E8EF] bg-white py-[2px] pl-[2px] pr-2.5 font-inter text-[12px] font-semibold text-[#2C3357]">
      <span className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full bg-[#E4E9F6] font-inter text-[9.5px] font-bold text-[#1D2951]">
        {initialsOf(name)}
      </span>
      <span className="truncate">{name}</span>
    </span>
  ) : null;

const DateTile = ({ date, accent, tint, big }) => {
  const d = dayjs(date);
  if (!date || !d.isValid()) return null;
  return (
    <div
      className={`flex flex-none flex-col items-center justify-center rounded-[12px] ${
        big ? 'w-[60px] py-2' : 'w-[50px] py-1.5'
      }`}
      style={{ background: tint, color: accent }}
    >
      <span className="font-inter text-[10px] font-bold uppercase tracking-[0.6px] opacity-80">
        {d.format('ddd')}
      </span>
      <span className={`font-inter font-bold leading-none ${big ? 'text-[24px]' : 'text-[20px]'}`}>
        {d.format('D')}
      </span>
      <span className="font-inter text-[10px] font-semibold uppercase tracking-[0.6px] opacity-80">
        {d.format('MMM')}
      </span>
    </div>
  );
};

const SectionHeading = ({ children, count }) => (
  <div className="flex items-center gap-2 text-left">
    <h3 className="m-0 font-inter text-[15px] font-bold text-[#14213D]">{children}</h3>
    {count === undefined ? null : (
      <span className="flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-[#E4E9F6] px-1.5 font-inter text-[11.5px] font-bold text-[#1D2951]">
        {count}
      </span>
    )}
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-6" role="status">
    <div className="h-7 w-7 animate-spin rounded-full border-[3px] border-[#E3E8EF] border-t-[#1D2951]" />
  </div>
);

const EmptyNote = ({ children }) => (
  <div className="px-4 py-5 text-left font-inter text-[13px] text-[#8A94A4]">{children}</div>
);

// One appointment in the Upcoming / Past lists. `onSelect` is the card's
// existing handleCardClick call, passed in from the list below.
const ApptRow = ({ row, selected, onSelect }) => {
  const cat = categoryOf(row?.type);
  const when = relativeWhen(row?.startdate);
  return (
    <div
      onClick={onSelect}
      className={`flex min-w-0 cursor-pointer overflow-hidden rounded-[12px] bg-white text-left ${
        selected ? '' : 'shadow-sm hover:shadow-md'
      }`}
      style={
        selected
          ? { boxShadow: `0 0 0 2px ${cat.color}, 0 2px 10px rgba(16,24,40,0.08)` }
          : undefined
      }
    >
      <span className="w-[5px] flex-none" style={{ background: cat.color }} />
      <div className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3">
        <DateTile date={row?.startdate} accent={cat.color} tint={cat.tint} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-inter text-[14.5px] font-bold text-[#14213D]">
            {dayjs(row?.startdate).format('ddd, MMMM D')}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 font-inter text-[12.5px] text-[#64748B]">
            <ClockGlyph size={12} />
            <span className="truncate">
              {timeLabel(row?.starttime)}
              {when ? ` · ${when}` : ''}
            </span>
          </div>
          <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
            <TypeChip type={row?.type} />
            <ProviderChip name={row?.providername} />
          </div>
        </div>
        <div className="flex min-w-0 max-w-[40%] items-center justify-end">
          <StatusChip status={row?.visit_confirmation} />
        </div>
      </div>
    </div>
  );
};

const CreateAppointment = ({setOrderData,setIsAptcard,fetchScheduleData,isfutureappointment,
   setNavigateTo, setScreen ,appointmentCardopen ,selectedAppointment,flow=''}) => {
    const [selectedDate, setSelectedDate] = React.useState(dayjs());
    const [showAppointmentForm, setShowAppointmentForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false); 
    const [checkoutloading,setCheckoutLoading]=useState(false)
    const navigate=useNavigate()
    const [showTextArea, setShowTextArea] = useState(false);
    const[tableData,setTableData]=useState([])
    const [selectedCard, setSelectedCard] = useState();
    const practiseid = getPracticeId();
    const [orderOptions, setOrderOptions] = useState([]);
    const [isOrderLoading, setIsorderLoading] = useState(false); 
    const [openToast, setOpenToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('success');
    const [deleteloading,setDeleteloading]=useState(false)
    const [CheckoutConformation,setCheckoutconformation]=useState(false)
       const [selectedOrders, setSelectedOrders] = useState([]);
    const [provider, setProvider] = useState("");
    const [selectedSlot,setSelectedSlot]=useState('')
    const [openCancelModal, setOpenCancelModal] = useState(false);

    const[appointmentname,setAppointmentName]=useState('')
    const [providername,setProvidername]=useState('');
    const [appointmentType, setAppointmentType] = useState("");
    const [rowId,setRowId]=useState(null);
    const [isReschedule, setIsReschedule] = useState(false); 
const[isorderschedule,setOrderSchedule]=useState(false)
const[selectedscheduleorder,setSelectedScheduleOrder]=useState('')
    const handleDateChange = (prop) => {
        setSelectedDate(prop);
    };

    const handleaddnoteClick = () => {
      setShowTextArea(!showTextArea); // Toggle the text area visibility
    };

    const handleToastClose = () => {
      setOpenToast(false)
  };
  
    const toggleFormVisibility = () => {
      setShowAppointmentForm(!showAppointmentForm);
    };
  
    const fetchdata=async(dt)=>{
      setIsLoading(true)
    try {
      const appointmentsdata=await api.get(`/v1/${practiseid}/appointments/${selectedAppointment?.patientid}/future`
      //   ,{
      // "scheduledate": dt
      // }
    )
    if(appointmentsdata.status===200){
      setTableData(appointmentsdata.data)
    }else{
      setTableData([])
    }
      
    } catch (error) {
      console.log(error)
    }finally{
      setIsLoading(false)
    }
 }
// setScreen - 2-Visit Status , 3-Payment
 useEffect(()=>{
  document.title = `${practiceShortName()} - Scheduler`;
  // console.log("selectedAppointment ",selectedAppointment);
   if(appointmentCardopen){
    fetchdata(selectedDate.format('YYYY-MM-DD'))     
   }
 
 },[appointmentCardopen])

 const handleCardClick = (upcomming,id) => {
  setSelectedCard(id === selectedCard ? null : id);  // Toggle selection
  fetchOrderData({orderStartDate:upcomming.startdate})
  setRowId(upcomming)
  if(flow!=='db'){
               setOrderData(upcomming)
        }

};


 const fetchOrderData = async ({orderStartDate}) => {
  const formattedStartDate = dayjs(orderStartDate).format('YYYY-MM-DD');
  // console.log("fStartDate",formattedStartDate);
   setIsorderLoading(true)
  try {
    const response = await api.post(`/v1/${practiseid}/scheduler/patients/${selectedAppointment?.patientid}/get_orders`, {
      startdate: formattedStartDate, // Payload to send in POST request
    });
    if (response.status === 200) {
      setOrderOptions(response.data); // Set the order data on success
    } else {
      setOrderOptions([]); // Clear if response is not successful
    }
  } catch (error) {
    console.error(error);
    setOrderOptions([]); // In case of error, clear the options
  } finally {
    setIsorderLoading(false); // Stop loading
  }
};


  const deleteAppointment=async(rowId)=>{
  setDeleteloading(rowId)
  console.log('iddcs',rowId)
  try {
    const response=await api.delete(`/v1/${practiseid}/scheduler/cancelschedule`,{
     data: {
        "rowid": rowId.id,
        "notes": "test delete"
      }}
    )
    
    if(response.status==200){
      setToastMessage("schedule cancelled successfully");
      setToastSeverity('success');
      setOpenToast(true);
  
      fetchdata()
    }else{
      setToastMessage('Failed to Cancel schedule. Please try again.');
      setToastSeverity('info');
      setOpenToast(true);
    }
    
  } catch (error) {
    console.log(error)
    setToastMessage('Something Went Wrong');
    setToastSeverity('error');
    setOpenToast(true);
  }finally{
    setDeleteloading(false)
  }
  
  }
  useEffect(() => {
    if (tableData && tableData.length > 0) {
      // Automatically call handleCardClick for the first appointment (index 0)
      handleCardClick(tableData[0],0 );
      setSelectedCard(0)

      console.log("DDDDDDDDDDDDDDD ", {
  selectedAppointment,
  tableDataItem: tableData[selectedCard],
  selectedCard,
  checkoutloading,
  isfutureappointment,
  flow,
  orderOptionsLength: orderOptions?.length,
  isToday: dayjs(tableData[selectedCard]?.startdate)
    .isSame(dayjs(), 'day')
});
    }
  }, [tableData]); 

  const SelectDate = new Date(selectedAppointment.startdate);
  const formattedDate = SelectDate.getFullYear() + '-' 
                       + (SelectDate.getMonth() + 1).toString().padStart(2, '0') + '-'
                       + SelectDate.getDate().toString().padStart(2, '0');

const checkoutuser=async()=>{

  try {
    setCheckoutLoading(true)
      // const response = await api.put(`/v1/${practiseid}/appointments/visit-purpose/update`, {
      //   "itemVal": 'Checked Out', // Pass the selected status value
      //   "dtype": ['dtvisit'],
      //   "app_id": [selectedAppointment.appointmentid],
      //   "cid": [selectedAppointment.AID],
      //   "searchdate": formattedDate
      // });
const matchingAppointments = tableData.filter(item =>
  item.startdate?.slice(0, 10) === selectedAppointment.startdate?.slice(0, 10)
);

const app_id = matchingAppointments.map(item => item.appointmentid);
const cid = matchingAppointments.map(item => item.id); // Make sure AID exists
const dtype = Array(app_id.length).fill("dtvisit");

const response = await api.put(`/v1/${practiseid}/appointments/visit-purpose/update`, {
  itemVal:Array(app_id.length).fill("Checked Out") ,
  dtype,
  app_id,
  cid,
  searchdate: formattedDate,
});

      if (response.status === 200) {

        setToastMessage(" Visit Status Updated successfully");
        setToastSeverity('success');
        setOpenToast(true);
        if(flow!=='db'){
               fetchScheduleData(selectedDate);
        }
           
 setTimeout(() => {
  if(flow!=='db'){
              setIsAptcard(false);
        }
 
}, 1500);
      } else {
        console.error("Update failed.");
        setToastMessage('Failed to Update Visit Status. Please try again.');
        setToastSeverity('info');
        setOpenToast(true);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setToastMessage('Something Went Wrong');
      setToastSeverity('error');
      setOpenToast(true);
    }finally{
      setCheckoutLoading(false)
    }
}
useEffect(()=>{
console.log( tableData[selectedCard])
},[selectedCard])

let disablebuttons=[ "Ready for Triage","Checked Out", 'In-Office - Confirmed', "Tele - Confirmed","Tele - left VM","In-Office - left VM","Awaiting Check In","Ready for Provider",'Processing']

const recordingProvider = selectedAppointment?.signintime?.BlinkscribeRecordingBy ?? null;
console.log("recordingProvider ", recordingProvider);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center items-start bg-black bg-opacity-50 p-0 sm:p-4 lg:p-6"
      onClick={() => setScreen(0)}
    >
      {/* Overlay */}


      {/* Popup Content */}
      <div
        className="relative z-10 flex h-full w-full max-w-[1400px] flex-col overflow-hidden bg-white text-left shadow-2xl sm:rounded-[18px]"
        onClick={(e) => {
            e.stopPropagation()
            // setScreen(0)
        }
        } // Prevent closing when clicking inside the popup

      >
        {/* Header - who this panel is about */}
        <div className="flex flex-none items-start justify-between gap-3 border-b border-[#E7ECF3] bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div
              onClick={() => {
  navigate('/landing', {
    state: { fromPatientId: selectedAppointment.patientid }
  });
}}
              title="Open on the dashboard"
              className="flex h-[52px] w-[52px] flex-none cursor-pointer items-center justify-center overflow-hidden rounded-[14px] bg-[#E4E9F6] font-inter text-[18px] font-bold text-[#1D2951] sm:h-[58px] sm:w-[58px] sm:text-[20px]"
            >
              {patientPhotoUrl(selectedAppointment.patientid) ? (
                <img
                  onError={(e) => e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAM1BMVEXk5ueutLeqsbTn6eqpr7PJzc/j5ebf4eLZ3N2wtrnBxsjN0NLGysy6v8HT1tissra8wMNxTKO9AAAFDklEQVR4nO2d3XqDIAxAlfivoO//tEOZWzvbVTEpic252W3PF0gAIcsyRVEURVEURVEURVEURVEURVEURVEURVEURVEURflgAFL/AirAqzXO9R7XNBVcy9TbuMHmxjN6lr92cNVVLKEurVfK/zCORVvW8iUBnC02dj+Wpu0z0Y6QlaN5phcwZqjkOkK5HZyPAjkIjSO4fIdfcOwFKkJlX4zPu7Ha1tIcwR3wWxyFhRG6g4Je0YpSPDJCV8a2Sv2zd1O1x/2WMDZCwljH+clRrHfWCLGK8REMiql//2si5+DKWKcWeAGcFMzzNrXC/0TUwQ2s6+LhlcwjTMlYsUIQzPOCb7YBiyHopyLXIEKPEkI/TgeuiidK/R9FniUDOjRDpvm0RhqjMyyXNjDhCfIMYl1gGjIMIuYsnGEYRMRZOMMunaLVwpWRW008v6fYKDIzxCwVAeNSO90BJW6emelYBRF/kHpYGVaoxTDAaxOFsfP9y8hpJ4xd7gOcij7JNGQ1EYFgkPJa1jQEiYZXRaRINKxSDUW9n+FT82lSKadkiru9/4XPqSLWOekGPoY05TAvLm9orm+YWuwHoBHkZKijNBJGmeb61eL6Ff/6q7bLr7yvv3vKGhpDRjvgjGaPz+gUg6YgcvpyAR2FIZ9U6nEEyZRTovmEU32KichpGn7C17XrfyH9gK/c0CMP05HZIM2uf9sEveizKveBy9/6Qt7o89ne33D525cfcIMW6ab+TMEukQbQbu+xu7X3A9bChmWaCeAkG17bpntwXgWxHaMzGPmUaR5dQZiKqRVeUZ3047fi3nAu28h4CHxCsZAgmEH8Y27jJAhm8c+5RQzRQNVGhVFSfxOYIjp/pP7RxzjevYXVGf4eLt+BJ1vCuLuLkrgABgCGXZ2wik5uty+oBvNirI6mkzhAf4Gsb58Hcm67Jzd+KwD10BYPLL3e0MjvKrgAULnOfveF/O4N2Xb9BZom3gJes3F9X5Zze8/6Yt09b4CrqsEjUv8oFBaR2rl+6CZr2xVrp24o/WitBKuGrrpl1+bFkmK2qXTON4VpbdfLa7o7y/WdLxG7lm2Lqh2clOwTegbvc/vj2U78CwhA87Bn8G5Nk3eOb0Nsr9flz3sG78UUtue4kpv1xvjg3TMay62BMlTlP+vrOMnJsRmt/ze0jsfkPPYdAH57hK+34PeOyc8XIXu5xT2HsUkdZz+adwg8HGFfQ3K5jtDvbUiO4Di9/ywHGrL88pDizZ++oTp+an+SMX/ndymUCwmHMdO7yuOx83pUx/eEMU0AvxWndwgidAqOZ8ypCwdEfvvEo6D9HwpA8wzvmOJEqAg9ySu8g4x0Hb9hSB/BANEKJ+LbPBU0lzbAJs4xt1AoshKkUGQmiH8/jJ0gdhTTLmSegHlPE0oOdXALnqDjKYh3px//fSgSWG8UqfrrIICzYYSJXRr9BSPbpNzw7gBjKjKOYI7ReIGqQRIap5+5MdjyvuDkExvGeXSlONWZAP3/AZBwJohU7QJRGU+cTVH18ELmRPNBmibW6MT/k1b0XhdkRBvyT6SB6EYv/GvhSmRNpGngRULsAlxMCGNXp7w3FfdEbTEEDdLI9TdIKRUzUesa3I461ER8cpNT7gMRhpKmYVS9ELOgCUQsa4SsulciKiLbY+AnHD8cpuhISsnxpamI84sbDq9qYJgf8wiiOBrC7Ml7M7ZECCqKoiiKoiiKoiiKoijv5AvJxlZRyNWWLwAAAABJRU5ErkJggg=='}
                  src={patientPhotoUrl(selectedAppointment.patientid)}
                  alt='%/'
                  className="h-full w-full object-cover"
                />
              ) : (
                initialsOf(`${selectedAppointment.firstname || ''} ${selectedAppointment.lastname || ''}`)
              )}
            </div>

            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <div
                  className="min-w-0 max-w-full cursor-pointer truncate font-inter text-[20px] font-bold leading-tight text-[#14213D] sm:text-[26px]"
                  onClick={() => {
  navigate('/landing', {
    state: { fromPatientId: selectedAppointment.patientid }
  });
}}
                >
                  {selectedAppointment.firstname} {selectedAppointment.lastname}
                </div>
                <div className="flex-none rounded-full bg-[#F1F3F7] px-2 py-[2px] font-inter text-[12px] font-semibold text-[#64748B]">
                  #{selectedAppointment.patientid}
                </div>
              </div>

              <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 font-inter text-[12.5px] text-[#64748B] sm:text-[13px]">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <CalendarGlyph size={13} />
                  <span className="truncate">DOB {selectedAppointment.dob}</span>
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <PhoneGlyph size={13} />
                  <span className="truncate">{selectedAppointment.phone}</span>
                </span>
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 sm:max-w-[320px]">
                  <ShieldGlyph size={13} />
                  <span className="truncate">{selectedAppointment.insurancename}</span>
                </span>
              </div>
            </div>
          </div>

          <div className='flex flex-none gap-2'>

 {/* Close Icon Button */}
         <IconButton
            edge="end"
            aria-label="close"
            onClick={() => {
              if(flow=='db'){
setScreen(false)
setIsAptcard(false)
return
              }
              setScreen(0)}}
            style={{ color: '#64748B', width: 38, height: 38, borderRadius: 10, border: '1px solid #E3E8EF' }}
          >
            <CloseIcon style={{ width: 18, height: 18 }} />
          </IconButton>

</div>

        </div>

        {/* Action row */}
<div className="flex w-full flex-none flex-wrap items-center justify-between gap-2 border-b border-[#E7ECF3] bg-white px-4 py-3 sm:px-6">


      <div onClick={() => {
          setShowAppointmentForm(true);
          setOrderSchedule(false)
       setIsReschedule(false);

      }}
      className={`cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#1D2951] px-4 py-2 font-inter text-[13px] font-semibold text-white shadow-sm transition-colors duration-300 ease-in-out hover:bg-[#14213D]`}
    >
      <PlusGlyph size={15} />
      Create appointment
    </div>



  <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
  {/* <div
  onClick={() => {
    if (!disablebuttons.includes(selectedAppointment?.visit_confirmation)&&
     tableData[selectedCard]?.visit_confirmation
 !== "Cancelled") {

    setIsReschedule(true);
      setShowAppointmentForm(true);
    
      setOrderSchedule(false)
    }
  }}
  className={`rounded mx-2 flex items-center gap-1 font-inter px-3 text-[12px] bg-[#DBEAFE] text-[#7E22CE] shadow-md truncate
    ${disablebuttons.includes(selectedAppointment?.visit_confirmation)||
      tableData[selectedCard]?.visit_confirmation
 === "Cancelled"
      ? "opacity-50 cursor-not-allowed pointer-events-none"
      : "cursor-pointer"
    }`}
>
  <img src={reschedule} className="w-4 h-4" alt="reschedule icon" />
  <div>Reschedule</div>
</div> */}
<div
  onClick={() => {
    const visitStatus = tableData[selectedCard]?.visit_confirmation;

    if (
      (tableData[selectedCard]=== "" || !disablebuttons.includes(visitStatus)) &&
      tableData[selectedCard]?.visit_confirmation !== "Cancelled"
    ) {
      setIsReschedule(true);
      setShowAppointmentForm(true);
      setOrderSchedule(false);
    }
  }}
  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#E3E8EF] bg-white px-3.5 py-2 font-inter text-[13px] font-semibold text-[#2C3357] shadow-sm hover:bg-[#F7F9FC]
    ${
      (tableData[selectedCard]?.visit_confirmation !== "" &&
        disablebuttons.includes(tableData[selectedCard]?.visit_confirmation)) ||
      tableData[selectedCard]?.visit_confirmation === "Cancelled"
        ? "opacity-50 cursor-not-allowed pointer-events-none"
        : "cursor-pointer"
    }`}
>
  <img src={reschedule} className="w-4 h-4" alt="reschedule icon" />
  <span>Reschedule</span>
</div>

 <Tooltip title={orderOptions?.length === 0 ? 'No orders found' : ''}>
<div
  onClick={(e) => {
  if (
  tableData[selectedCard]?.visit_confirmation === "Cancelled" ||
  !dayjs(tableData[selectedCard]?.startdate).isSame(dayjs(), 'day') ||
  checkoutloading ||
  selectedAppointment?.visit_confirmation === "Checked Out"
) {
  return;
}

 if(isfutureappointment)return
    e.stopPropagation();

    if (orderOptions?.length === 0) {
      setCheckoutconformation(true);
    } else {
      if(flow!=='db'){
               setNavigateTo(1);
                  setScreen(4);
        }else{
          setScreen(0)
        }


    }
  }}
  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 font-inter text-[13px] font-semibold shadow-sm
    ${
      checkoutloading || isfutureappointment || selectedAppointment?.visit_confirmation === "Checked Out"||   tableData[selectedCard]?.visit_confirmation
 === "Cancelled"|| !dayjs(tableData[selectedCard]?.startdate).isSame(dayjs(), 'day')
        ? "border-[#E7ECF3] bg-[#F1F3F7] text-[#9AA4B5] cursor-not-allowed"
        : "border-[#E3E8EF] bg-white text-[#1D4ED8] cursor-pointer hover:bg-[#F7F9FC]"
    }`}
  style={{ opacity: checkoutloading || selectedAppointment?.visit_confirmation === "Checked Out" ? 0.6 : 1 }}
>
  <img
    src={checkout}
    alt="checkout"
    className={`w-4 h-4 ${checkoutloading ? "animate-spin" : ""}`}
  />
  <span>Checkout</span>
</div>


</Tooltip>
<div
  onClick={() => {
if (
  (flow !== "db" && isfutureappointment) ||
  !dayjs(tableData[selectedCard]?.startdate).isSame(dayjs(), 'day') ||
  tableData[selectedCard]?.visit_confirmation === "Cancelled" ||
  tableData.length === 0 ||
  selectedAppointment?.visit_confirmation === "Checked Out"
) {
  return;
}

    setScreen(3);
  }}
  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#E3E8EF] bg-white px-3.5 py-2 font-inter text-[13px] font-semibold text-[#15803D] shadow-sm hover:bg-[#F7F9FC]
    ${
      tableData.length === 0 || (flow !== "db" && isfutureappointment)|| selectedAppointment?.visit_confirmation === "Checked Out"||   tableData[selectedCard]?.visit_confirmation
 === "Cancelled"||!dayjs(tableData[selectedCard]?.startdate).isSame(dayjs(), 'day')

        ? "cursor-not-allowed opacity-50 pointer-events-none"
        : "cursor-pointer"
    }`}
>
  <img src={payment} alt="$?" className="w-4 h-4" />
  <span>Take payment</span>
</div>

<div
  onClick={() => {
   const visitStatus = tableData[selectedCard]?.visit_confirmation;

  if (visitStatus === "Cancelled") return;

  if (!disablebuttons.includes(visitStatus || "")) {
    setOpenCancelModal(true);   // 👈 open modal instead
  }
  }}
  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#FFC9C9] bg-white px-3.5 py-2 font-inter text-[13px] font-semibold text-[#C10007] shadow-sm hover:bg-[#FEF2F2]
    ${   tableData[selectedCard]?.visit_confirmation &&
      (disablebuttons.includes(tableData[selectedCard]?.visit_confirmation) ||
        tableData[selectedCard]?.visit_confirmation === "Cancelled")
      ? "opacity-50 cursor-not-allowed pointer-events-none"
      : "cursor-pointer"
    }`}
>
  <CloseIcon className="w-3 h-3" style={{ width: '15px' }} />
  <span>Cancel</span>
</div>


  </div>
 </div>


{/* Body */}
<div className='flex-1 overflow-y-auto custom-scrollbar bg-[#F6F8FB] px-3 py-4 sm:px-5 sm:py-5'>
<div className='mx-auto w-full max-w-[1320px]'>
<div className='flex w-full flex-col gap-4 lg:flex-row lg:items-start'>

{/* Left column - this visit, what is coming, what has been */}
<div className='flex w-full min-w-0 flex-col gap-3 lg:w-[57%]'>

  <div className="flex min-w-0 overflow-hidden rounded-[14px] bg-white shadow-sm">
    <span
      className="w-[6px] flex-none"
      style={{ background: categoryOf(selectedAppointment.type).color }}
    />
    <div className="flex min-w-0 flex-1 items-start gap-3 p-4">
      <DateTile
        big
        date={selectedAppointment.startdate}
        accent={categoryOf(selectedAppointment.type).color}
        tint={categoryOf(selectedAppointment.type).tint}
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <TypeChip type={selectedAppointment.type} />
          <StatusChip status={selectedAppointment.visit_confirmation} />
        </div>
        <div className="mt-1.5 font-inter text-[17px] font-bold leading-tight text-[#14213D] sm:text-[20px]">
          {dayjs(selectedAppointment.startdate).isValid()
            ? dayjs(selectedAppointment.startdate).format('dddd, MMMM D, YYYY')
            : ''}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1.5 font-inter text-[13px] text-[#64748B]">
          <ClockGlyph size={13} />
          <span className="truncate">
            {timeLabel(selectedAppointment.starttime)}
            {relativeWhen(selectedAppointment.startdate)
              ? ` · ${relativeWhen(selectedAppointment.startdate)}`
              : ''}
          </span>
        </div>
        <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-2">
          <ProviderChip name={selectedAppointment.providername} />
          {recordingProvider && (
            <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border border-[#FDE2B3] bg-[#FEF6E7] px-2.5 py-[3px] font-inter text-[12px] font-semibold text-[#92400E]">
              <MicIcon style={{ width: 14, height: 14 }} />
              <span className="truncate">{recordingProvider}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  </div>

  {isLoading ? (
    <Spinner />
  ) : tableData.length === 0 ? (
    <div className="rounded-[12px] border border-dashed border-[#D9E1EC] bg-white px-4 py-6 text-center font-inter text-[13px] text-[#8A94A4]">
      No Data Found
    </div>
  ) : (
    <>
      <SectionHeading count={tableData.filter((row) => !isPastRow(row)).length}>
        Upcoming appointments
      </SectionHeading>
      {tableData.filter((row) => !isPastRow(row)).length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-[#D9E1EC] bg-white px-4 py-5 text-left font-inter text-[13px] text-[#8A94A4]">
          No upcoming appointments
        </div>
      ) : (
        tableData.map((upcomming, id) =>
          isPastRow(upcomming) ? null : (
            <ApptRow
              key={id}
              row={upcomming}
              selected={selectedCard === id}
              onSelect={() => handleCardClick(upcomming,id)}
            />
          )
        )
      )}

      {tableData.filter((row) => isPastRow(row)).length > 0 ? (
        <>
          <SectionHeading count={tableData.filter((row) => isPastRow(row)).length}>
            Past visits
          </SectionHeading>
          {tableData.map((upcomming, id) =>
            isPastRow(upcomming) ? (
              <ApptRow
                key={id}
                row={upcomming}
                selected={selectedCard === id}
                onSelect={() => handleCardClick(upcomming,id)}
              />
            ) : null
          )}
        </>
      ) : null}
    </>
  )}

</div>

{/* Right column - orders */}
<div className='flex w-full min-w-0 flex-col gap-3 lg:w-[43%]'>
<SectionHeading>Orders</SectionHeading>

<div className="overflow-hidden rounded-[14px] bg-white shadow-sm">
  {/* Today Orders */}
  <div className="flex items-center gap-3 border-b border-[#EDF1F7] px-4 py-3">
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-[#1D2951] text-white">
      <ClipboardGlyph size={16} />
    </span>
    <span className="min-w-0 flex-1 truncate text-left font-inter text-[15px] font-bold text-[#14213D]">
      Today's orders
    </span>
  </div>
  <div className='max-h-[400px] overflow-auto custom-scrollbar'>
 {isOrderLoading ? (
    <Spinner />
  ) : orderOptions.filter(order => order.schedule === "f" && order.ordertype !== "Prescription" && order.ordertype !== "Notes").length > 0 ? (
    orderOptions.filter(order => order.schedule === "f" && order.ordertype !== "Prescription" && order.ordertype !== "Notes").map((order, index) => (
      <div key={index} className="flex items-start justify-between gap-3 border-b border-[#F1F4F9] px-4 py-3 last:border-b-0">
        <div className="min-w-0 flex-1">
          <div className="truncate text-left font-inter text-[14px] font-bold text-[#14213D]">
            {order.ordername}
          </div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-left font-inter text-[12.5px] text-[#64748B]">
            <span className="truncate">
 {order.ordertype?.charAt(0).toUpperCase() + order.ordertype?.slice(1)}
            </span>
            <span className="whitespace-nowrap">
        {(() => {
    const dateStr = order?.ts?.split(' ')[0]; // safely access date part
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return '';
    }

    const [year, month, day] = dateStr.split('-');
    return `${+month}/${+day}/${year}`;
  })()}

            </span>
          </div>
        </div>
          <div className='flex flex-none items-center gap-2'>

 <div
  className={`text-xs font-medium px-2 py-[2px] rounded-full border-[1px] ${
    order.status === 'CREATED'
      ? 'text-[#CA8A04] border-[#CA8A04]'
      : 'text-[#16A34A] border-[#16A34A]'
  }`}
>
  {order.status === 'CREATED' ? 'Pending' : order.status}
</div>
{order.ordertype=='Schedule-RTO'?<div
onClick={()=>{
     setShowAppointmentForm(true);
          setOrderSchedule(true)
       setIsReschedule(false);
       setSelectedScheduleOrder(order)
       setSelectedOrders([order])
}}
className='cursor-pointer'>

  {calendericonblue}
</div>:<></>}


          </div>
      </div>
    ))
  ) : (
    <EmptyNote>No today's orders yet</EmptyNote>
  )}


  </div>


  {/* Schedule Orders */}
</div>



<div className="overflow-hidden rounded-[14px] bg-white shadow-sm">
  {/* Today Orders */}
  <div className="flex items-center gap-3 border-b border-[#EDF1F7] px-4 py-3">
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-[#50869A] text-white">
      <CalendarClockGlyph size={16} />
    </span>
    <span className="min-w-0 flex-1 truncate text-left font-inter text-[15px] font-bold text-[#14213D]">
      Scheduled orders
    </span>
  </div>
    <div className='max-h-[400px] overflow-auto custom-scrollbar'>
 {isOrderLoading ? (
    <Spinner />
  ) : orderOptions.filter(order => order.schedule === "t" && order.ordertype !== "Prescription" && order.ordertype !== "Notes").length > 0 ? (
    orderOptions.filter(order => order.schedule === "t" && order.ordertype !== "Prescription" && order.ordertype !== "Notes").map((order, index) => (
      <div key={index} className="flex items-start justify-between gap-3 border-b border-[#F1F4F9] px-4 py-3 last:border-b-0">
        <div className="min-w-0 flex-1">
          <div className="truncate text-left font-inter text-[14px] font-bold text-[#14213D]">
            {order.ordername}
          </div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-left font-inter text-[12.5px] text-[#64748B]">
            <span className="truncate">
          {order.ordertype?.charAt(0).toUpperCase() + order.ordertype?.slice(1)}
            </span>
            <span className="whitespace-nowrap">
              {(() => {
    const dateStr = order?.ts?.split(' ')[0]; // safely access date part
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return '';
    }

    const [year, month, day] = dateStr.split('-');
    return `${+month}/${+day}/${year}`;
  })()}
            </span>
          </div>
        </div>
          <div className='flex flex-none gap-2 items-center'>
        <div
  className={`text-xs font-medium px-2 py-[2px] rounded-full border-[1px] ${
    order.status === 'CREATED'
      ? 'text-[#CA8A04] border-[#CA8A04]'
      : 'text-[#16A34A] border-[#16A34A]'
  }`}
>
  {order.status === 'CREATED' ? 'Pending' : order.status}
</div>
{order.status!='COMPLETED'&&<div
onClick={()=>{
     setShowAppointmentForm(true);
          setOrderSchedule(true)
       setIsReschedule(false);
       setSelectedScheduleOrder(order)
       setSelectedOrders([order])
}}
className='cursor-pointer'>

  {calendericonblue}
</div>}



          </div>
      </div>
    ))
  ) : (
    <EmptyNote>No scheduled orders yet</EmptyNote>
  )}


  </div>

  {/* Schedule Orders */}
</div>

</div>

</div>

{/* The create / reschedule form is an in-flow block, not an overlay, so it
    lives inside the scrolling body - outside it the panel would clip it. */}
{/* Create Appointerment */}
{showAppointmentForm &&
 <AppointmentCard

 fetchdata={()=>{
      fetchdata(selectedDate.format('YYYY-MM-DD'))  
 }}
 selectedOrders={selectedOrders} setSelectedOrders={setSelectedOrders}
 handleaddnoteClick={handleaddnoteClick}
  handleDateChange={handleDateChange} 
  selectedDate={selectedDate} 
  showTextArea={showTextArea}
  selectedAppointment={selectedAppointment}
  setSelectedDate={setSelectedDate}
tableData={tableData}
selectedCard={selectedCard}
orderOptions={orderOptions}
  provider={provider}
  setProvider={setProvider}
  selectedSlot={selectedSlot}
  setSelectedSlot={setSelectedSlot}
  appointmentname={appointmentname}
  setAppointmentName={setAppointmentName}
  setAppointmentType={setAppointmentType}
  appointmentType={appointmentType}
  providername={providername}
  setProvidername={setProvidername}
  isReschedule={isReschedule}
  rowId={rowId}
  isorderschedule={isorderschedule}
  selectedscheduleorder={selectedscheduleorder}
  /> 
  } 
</div>
</div>

  <ConfirmCheckoutModal
  isOpen={CheckoutConformation}
  onClose={() => setCheckoutconformation(false)}
  onCheckout={() => {
 checkoutuser();
    setCheckoutconformation(false);
  }}
/>
<ConfirmCancelModal
  open={openCancelModal}
  onClose={() => setOpenCancelModal(false)}
  onConfirm={() => {
    deleteAppointment(rowId);
    setOpenCancelModal(false);
  }}
/>

    <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
      </div>
    </div>
  );
};

export default CreateAppointment;
